import { ClozeTestService } from "./../service/cloze-test.service";
import { AnswerDto } from "./../dto/answer-dto";
import { Component, OnInit, OnDestroy, SimpleChanges, OnChanges, output, input, inject } from "@angular/core";
import { interval, Subscription } from "rxjs";
import { ClozeTestStyleModel } from "../model/cloze-test-style-model";
import { InputModel } from "../model/input-model";
import { ClozeTestModel } from "../model/cloze-test-model";
import { NgClass, NgStyle } from "@angular/common";
import { TranslatePipe } from "@ngx-translate/core";
import { ExerciseIndicatorComponent } from "../exercise-indicator/exercise-indicator.component";
import { InputComponent } from "./input/input.component";

const FIRST_KEYBOARD_ROW = ["w", "e", "r", "t", "z", "u", "i", "o", "p", "ü"];

const KEYBOARD_ROWS = [
  ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ö", "ä"],
  ["y", "x", "c", "v", "b", "n", "m", "ß"],
];

@Component({
  selector: "onset-cloze-test-exercise",
  templateUrl: "./exercise.component.html",
  standalone: true,
  styleUrls: ["./exercise.component.scss"],
  imports: [NgClass, TranslatePipe, NgStyle, ExerciseIndicatorComponent, InputComponent],
})
export class ExerciseComponent implements OnInit, OnDestroy, OnChanges {
  readonly #clozeTestService = inject(ClozeTestService);
  readonly DONE_LABEL_MOBILE = "FERTIG";
  readonly DONE_LABEL_DESKTOP = "TEXT FERTIG BEARBEITEN";
  readonly NEXT_LABEL_MOBILE = "WEITER";
  readonly NEXT_LABEL_DESKTOP = "WEITER ZUM NÄCHSTEN LÜCKENTEXT";
  readonly FIRST_KEYBOARD_ROW = FIRST_KEYBOARD_ROW;
  readonly KEYBOARD_ROWS = KEYBOARD_ROWS;

  readonly nextExercise = output<AnswerDto[]>();
  readonly endTest = output<AnswerDto[]>();

  readonly title = input("");
  readonly content = input("");
  readonly isLastExercise = input(false);
  readonly currentExcerciseNumber = input(0);
  readonly lastExerciseNumber = input<number | null>(null);
  readonly clozeTestStyleModel = input<ClozeTestStyleModel>({} as ClozeTestStyleModel);
  readonly bookingId = input<number | null>(null);
  readonly exerciseId = input<number | null>(null);
  readonly cTestLogo = input("");
  readonly cTestButtonColor = input("");
  readonly mobileMode = input(false);
  readonly totalExercise = input(0);
  readonly clozeTestModel = input<ClozeTestModel>({} as ClozeTestModel);

  timeSubscription!: Subscription;
  timeLimit = 300;
  // timeLimit = 3000;
  timeLeft = this.timeLimit;
  limitWhenToShowTime = 60;
  interval$ = interval(1000);
  lineHeight = 2;
  readyCheckbox = false;

  inputs: InputModel[] = [];

  doneLabel = "";
  nextLabel = "";
  startTime = Date.now();

  ngOnInit() {
    this.subscribeToInterval();
    if (this.mobileMode()) {
      this.doneLabel = this.DONE_LABEL_MOBILE;
      this.nextLabel = this.NEXT_LABEL_MOBILE;
      this.lineHeight = 1.75;
    } else {
      this.doneLabel = this.DONE_LABEL_DESKTOP;
      this.nextLabel = this.NEXT_LABEL_DESKTOP;
    }
  }

  ngOnDestroy() {
    this.timeSubscription.unsubscribe();
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges["content"].currentValue) {
      this.inputs = [];
      this.splitContent();
      // set inputNumber to first cTest input
      this.#clozeTestService.inputNumber = this.inputs[0].id as number;
    }
    if (simpleChanges["exerciseId"].currentValue) {
      this.#clozeTestService.updateStartTimeOfAnExercise(
        this.clozeTestModel().onsetServiceUrl,
        this.bookingId() as number,
        this.exerciseId() as number
      );
      console.log(`update start time boooking id: ${this.bookingId()} and exercise id : ${this.exerciseId()} `);
    }
  }

  public removeLastCharacter(event: TouchEvent): void {
    this.#clozeTestService.removeLastCharacterFromInput();
    const el = event.srcElement;
    (el as Element)?.classList.add("mobile-keyboard-button-active");
    event.stopPropagation();
    event.preventDefault();
  }

  public goToPreviousInput(event: TouchEvent): void {
    if (!this.#clozeTestService.inputNumber) {
      this.#clozeTestService.inputNumber = this.inputs[0].id as number;
    } else if (this.#clozeTestService.inputNumber > 1) {
      const currentInputId = this.#clozeTestService.inputNumber;
      const inputArrayIndex = this.inputs.findIndex((i) => i.id === currentInputId);
      if (this.inputs[inputArrayIndex - 1]) {
        this.#clozeTestService.inputNumber = this.inputs[inputArrayIndex - 1].id as number;
      }
    }
    event.stopPropagation();
    event.preventDefault();
  }

  public goToNextInput(event: TouchEvent): void {
    if (!this.#clozeTestService.inputNumber) {
      this.#clozeTestService.inputNumber = this.inputs[0].id as number;
    } else {
      const currentInputId = this.#clozeTestService.inputNumber;
      const inputArrayIndex = this.inputs.findIndex((i) => i.id === currentInputId);
      if (this.inputs[inputArrayIndex + 1]) {
        this.#clozeTestService.inputNumber = this.inputs[inputArrayIndex + 1].id as number;
      }
    }
    event.stopPropagation();
    event.preventDefault();
  }

  public onNextExercise(): void {
    this.#clozeTestService.updateEndTimeOfAnExercise(
      this.clozeTestModel().onsetServiceUrl,
      this.bookingId() as number,
      this.exerciseId() as number
    );
    console.log(`update end time boooking id: ${this.bookingId} and exercise id : ${this.exerciseId} `);
    this.readyCheckbox = false;
    this.timeLeft = this.timeLimit;
    this.startTime = Date.now();
    this.nextExercise.emit(this.inputs.map((i) => i.answer).filter((a) => a.nr !== null));
  }

  public onTestEnd(): void {
    this.#clozeTestService.updateEndTimeOfAnExercise(
      this.clozeTestModel().onsetServiceUrl,
      this.bookingId() as number,
      this.exerciseId() as number
    );
    this.endTest.emit(this.inputs.map((i) => i.answer).filter((a) => a.nr !== null));
  }

  public assignCharacter(specialCharacter: string, event: Event): void {
    this.#clozeTestService.setSpecialCharacterToInput(specialCharacter);
    const el = event.srcElement;
    (el as Element).classList.add("mobile-keyboard-button-active");
    event.stopPropagation();
    event.preventDefault();
  }

  public onTouchend(event: TouchEvent): void {
    const el = event.srcElement;
    (el as Element).classList.remove("mobile-keyboard-button-active");
    event.stopPropagation();
    event.preventDefault();
  }

  public showTimer() {
    return this.timeLeft <= this.limitWhenToShowTime;
  }

  private subscribeToInterval(): void {
    this.timeSubscription = this.interval$.subscribe(() => {
      const delta = Date.now() - this.startTime;
      this.timeLeft = this.timeLimit - Math.floor(delta / 1000);
      if (this.timeLeft <= 0) {
        if (this.isLastExercise()) {
          this.onTestEnd();
        } else {
          this.onNextExercise();
        }
      }
    });
  }

  private splitContent(): void {
    const inputs = this.content().toString().split("</luecke>");
    inputs.forEach((i) => {
      const regex = new RegExp(/<luecke id="luecke_(.*)">/);
      const regex2 = new RegExp(/<luecke id='luecke_(.*)'>/);
      let id = "";
      // Extract id
      const matches = i.match(regex);
      const matches2 = i.match(regex2);
      if (matches) {
        id = matches[1];
      }
      if (matches2) {
        id = matches2[1];
      }

      // Delete all html tags
      i = i.replace(/<\/?[^>]+(>|$)/g, "");

      // Find last word
      let merged: string;
      let lastWord = "";
      if (id) {
        const splitted = i.split(" ");
        lastWord = splitted[splitted.length - 1];
        splitted.pop();
        merged = splitted.join(" ");
      } else {
        merged = i;
      }

      this.inputs.push({
        id: id,
        text: merged,
        lastWord: lastWord,
        answer: {
          buchungId: this.bookingId() as number,
          nr: id === null ? null : parseInt(id, 10),
          aufgabeId: this.exerciseId() as number,
          antwort: "",
        } as AnswerDto,
      } as InputModel);
    });
  }
}
